import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  MenuItem,
  FormControl,
  Select,
  Typography,
  IconButton,
  Button,
} from "@mui/material";
import { ChevronLeft, ChevronRight, CheckCircle } from "@mui/icons-material";
import ApiClient from "../../../apis/Api-client";
import { END_POINT } from "../../../utils/End-points";
import { useTranslation } from "react-i18next";
import {
  format,
  addWeeks,
  subWeeks,
  startOfWeek,
  endOfWeek,
  addMonths,
  subMonths,
} from "date-fns";
import CalendarWeekView from "../../common-components/Calendar/CalendarWeekView";
import { convertTo12Hour } from "../../../utils/timeUtils";

import AttendancePopup from "./AttendancePopup";
import LeavePopup from "./LeavePopup";
import {
  VIEW_MODES,
  FILTER_OPTIONS,
  ATTENDANCE_STATUS,
  DATE_FORMATS,
  SHIFT_TYPES,
} from "../../../utils/Constants";
const styles = {
  container: {
    p: { xs: 1.5, sm: 3 },
    bgcolor: "#fff",
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: 2,
    mb: { xs: 2, sm: 3 },
    flexDirection: { xs: "column", sm: "row" },
    width: { xs: "100%", sm: "auto" },
  },
  filterControl: {
    minWidth: { xs: "100%", sm: 220 },
  },
  placeholderText: {
    color: "#9E9E9E",
    fontWeight: 500,
    fontSize: { xs: "0.8rem", sm: "0.9rem" },
  },
  selectedText: {
    color: "#333",
    fontWeight: 600,
    fontSize: { xs: "0.8rem", sm: "0.9rem" },
  },
  select: {
    borderRadius: "8px",
    bgcolor: "#fff",
    ".MuiSelect-select": { py: 1.5, pl: 2 },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#F7A86E",
      borderWidth: "1px",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#F7A86E" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#F7A86E",
    },
  },
  dateNavigator: {
    display: "flex",
    alignItems: "center",
    gap: 1,
    bgcolor: "#F5F5F5",
    borderRadius: "8px",
    p: 0.5,
    border: "1px solid #E0E0E0",
  },
  dateLabel: {
    fontWeight: 600,
    fontSize: { xs: "0.8rem", sm: "0.9rem" },
    color: "#333",
    px: 1,
  },
  iconButton: { color: "#333" },
  viewToggle: (isActive) => ({
    minWidth: 60,
    textTransform: "none",
    fontWeight: 600,
    borderRadius: "8px",
    height: 36,
    ml: 1,
    bgcolor: isActive ? "#F7A86E" : "#f5f5f5",
    color: isActive ? "#fff" : "#757575",
    border: isActive ? "none" : "1px solid #E0E0E0",
    "&:hover": {
      bgcolor: isActive ? "#E2955E" : "#eeeeee",
    },
    boxShadow: isActive ? "0 2px 5px rgba(247, 168, 110, 0.4)" : "none",
  }),
  calendarContainer: {
    flex: 1,
    overflow: "hidden",
  },
  leaveEvent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    width: "100%",
    bgcolor: "#FFF3E0",
    p: 0.5,
  },
  leaveText: {
    fontSize: "1.5rem",
    fontWeight: 900,
    color: "#F57C00",
  },
  completedEvent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "space-between",
    height: "100%",
    width: "100%",
    bgcolor: "#fff",
    pt: 1,
  },
  checkIcon: {
    color: "#4CAF50",
    fontSize: "1.5rem",
  },
  timeBar: {
    width: "100%",
    bgcolor: "#E8F5E9",
    textAlign: "center",
    py: 0.5,
    mt: "auto",
  },
  timeText: {
    fontSize: "0.65rem",
    fontWeight: 600,
    color: "#4CAF50",
    lineHeight: 1,
  },
  ongoingEvent: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    height: "100%",
    width: "100%",
    bgcolor: "#66BB6A",
    color: "#fff",
    p: 1,
    borderRadius: "4px",
    borderLeft: "4px solid #388E3C",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },
  ongoingTimeText: {
    fontSize: "0.75rem",
    fontWeight: 700,
  },
};
const EmployeeAttendance = () => {
  const { t } = useTranslation();
  const { api } = ApiClient();
  const [employees, setEmployees] = useState([]);
  const [employeeFilter, setEmployeeFilter] = useState(FILTER_OPTIONS.ALL);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState(VIEW_MODES.WEEK);
  const [shifts, setShifts] = useState([]);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupData, setPopupData] = useState(null);
  const [isLeavePopupOpen, setIsLeavePopupOpen] = useState(false);
  const [leaveData, setLeaveData] = useState(null);
  const isWeekView = view === VIEW_MODES.WEEK;

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await api.get(END_POINT.EMPLOYEE.GET_ALL_EMPLOYEES);
      if (response.data && response.data.data) {
        setEmployees(response.data.data);
      }
    } catch (error) {
      console.error("Failed to fetch employees", error);
    }
  };

  const fetchAttendance = async () => {
    try {
      let start, end;
      if (isWeekView) {
        start = startOfWeek(currentDate, { weekStartsOn: 1 });
        end = endOfWeek(currentDate, { weekStartsOn: 1 });
      } else {
        start = startOfWeek(currentDate, { weekStartsOn: 1 });
        end = endOfWeek(currentDate, { weekStartsOn: 1 });
      }

      const startDateStr = format(start, DATE_FORMATS.API_DATE);
      const endDateStr = format(end, DATE_FORMATS.API_DATE);

      const response = await api.get(
        `${END_POINT.EMPLOYEE.GET_ATTENDANCE_RECORDS}?start_date=${startDateStr}&end_date=${endDateStr}`
      );

      if (response.data && response.data.data) {
        const attendanceRecords = response.data.data.map((record) => ({
          ...record,
          start_time: record.check_in_time,
          end_time: record.check_out_time || ATTENDANCE_STATUS.ONGOING,
          shift_type: SHIFT_TYPES.ATTENDANCE,
          color:
            record.status === ATTENDANCE_STATUS.COMPLETED
              ? "#4CAF50"
              : "#81C784",
          date: record.date,
          isAttendance: true,
          check_in_time: record.check_in_time,
          check_out_time: record.check_out_time,
          employee_id: record.employee_id,
          status: record.status,
          assignment_id: record.assignment_id,
          attendance_id: record.id,
        }));
        setShifts(attendanceRecords);
      }
    } catch (error) {
      console.error("Failed to fetch attendance", error);
    }
  };

  useEffect(() => {
    fetchAttendance();
  }, [currentDate, view]);

  const handlePrev = () => {
    if (isWeekView) setCurrentDate(subWeeks(currentDate, 1));
    else if (view === VIEW_MODES.MONTH)
      setCurrentDate(subMonths(currentDate, 1));
  };

  const handleNext = () => {
    if (isWeekView) setCurrentDate(addWeeks(currentDate, 1));
    else if (view === VIEW_MODES.MONTH)
      setCurrentDate(addMonths(currentDate, 1));
  };

  const dateLabel = useMemo(() => {
    if (isWeekView) {
      const start = startOfWeek(currentDate, { weekStartsOn: 1 });
      const end = endOfWeek(currentDate, { weekStartsOn: 1 });
      if (
        format(start, DATE_FORMATS.MONTH) === format(end, DATE_FORMATS.MONTH)
      ) {
        return `${format(start, DATE_FORMATS.DAY)}-${format(
          end,
          DATE_FORMATS.DAY
        )} ${format(end, DATE_FORMATS.DISPLAY_MONTH)}`;
      }
      return `${format(start, DATE_FORMATS.DISPLAY_DATE)} - ${format(
        end,
        DATE_FORMATS.DISPLAY_DATE_FULL
      )}`;
    }
    if (view === VIEW_MODES.MONTH)
      return format(currentDate, DATE_FORMATS.DISPLAY_MONTH);
    return "";
  }, [view, currentDate]);

  const filteredEmployees = useMemo(() => {
    if (employeeFilter === FILTER_OPTIONS.ALL) return employees;
    return employees.filter((e) => e.employee_id === employeeFilter);
  }, [employees, employeeFilter]);

  const handleAssignShift = async (data) => {
    if (data.shift_type === t("layout.attendance.add_attendance")) {
      try {
        const response = await api.get(
          `${END_POINT.EMPLOYEE.GET_SHIFT_ASSIGNMENTS}?employee_id=${data.employee_id}&date=${data.date}`
        );

        const employee = employees.find(
          (e) => e.employee_id === data.employee_id
        );
        const assignmentData =
          response.data && response.data.data && response.data.data.length > 0
            ? response.data.data[0]
            : {};

        setPopupData({
          ...data,
          employee: employee,
          shift_type:
            assignmentData.shift?.shift_type || assignmentData.shift_type || "",
          start_time:
            assignmentData.shift?.start_time || assignmentData.start_time || "",
          end_time:
            assignmentData.shift?.end_time || assignmentData.end_time || "",
          assignment_id: assignmentData.assignment_id,
          shift: assignmentData.shift,
        });
        setIsPopupOpen(true);
      } catch (error) {
        console.error("API Error:", error);
      }
    } else if (data.shift_type === t("layout.attendance.apply_leave")) {
      setLeaveData(data);
      setIsLeavePopupOpen(true);
    }
  };

  const handleLeaveSubmit = async (formData) => {
    try {
      const payload = {
        employee_id: leaveData.employee_id,
        status: ATTENDANCE_STATUS.LEAVE,
        from_date: formData.from_date,
        to_date: formData.to_date,
        reason: formData.reason,
      };
      await api.post(END_POINT.EMPLOYEE.CREATE_ATTENDANCE, payload);
      setIsLeavePopupOpen(false);
      fetchAttendance();
    } catch (error) {
      console.error("Leave Create Error", error);
    }
  };

  const handleDeleteShift = (id) => {};

  const handleShiftResize = (data) => {};

  const handleShiftClick = async (shift) => {
    if (shift.isAttendance) {
      try {
        const formattedDate = format(
          new Date(shift.date),
          DATE_FORMATS.API_DATE
        );
        const response = await api.get(
          `${END_POINT.EMPLOYEE.GET_SHIFT_ASSIGNMENTS}?employee_id=${shift.employee_id}&date=${formattedDate}`
        );
        const assignmentData =
          response.data && response.data.data && response.data.data.length > 0
            ? response.data.data[0]
            : {};

        const employee = employees.find(
          (e) => e.employee_id === shift.employee_id
        );

        setPopupData({
          ...shift,
          employee: employee,
          shift_type:
            assignmentData.shift?.shift_type ||
            assignmentData.shift_type ||
            shift.shift_type,
          start_time:
            assignmentData.shift?.start_time || assignmentData.start_time,
          end_time: assignmentData.shift?.end_time || assignmentData.end_time,
          assignment_id: assignmentData.assignment_id || shift.assignment_id,
          shift: assignmentData.shift,
        });
        setIsPopupOpen(true);
      } catch (error) {
        console.error("Failed to fetch shift assignment details", error);
      }
    }
  };

  const handlePopupSubmit = async (submissionData) => {
    try {
      if (submissionData.status === ATTENDANCE_STATUS.ONGOING) {
        await api.post(END_POINT.EMPLOYEE.CREATE_ATTENDANCE, submissionData);
      } else if (submissionData.status === ATTENDANCE_STATUS.COMPLETED) {
        await api.put(END_POINT.EMPLOYEE.CREATE_ATTENDANCE, submissionData);
      }

      fetchAttendance();
      setIsPopupOpen(false);
    } catch (error) {
      console.error("Submit Error", error);
    }
  };

  return (
    <Box sx={styles.container}>
      <AttendancePopup
        open={isPopupOpen}
        onClose={() => setIsPopupOpen(false)}
        data={popupData}
        onSubmit={handlePopupSubmit}
      />
      {/* Top Toolbar */}
      <Box sx={styles.toolbar}>
        {/* Employee Filter */}
        <FormControl sx={styles.filterControl} size="small">
          <Select
            value={employeeFilter}
            onChange={(e) => setEmployeeFilter(e.target.value)}
            displayEmpty
            renderValue={(selected) => {
              if (selected === FILTER_OPTIONS.ALL) {
                return (
                  <Typography sx={styles.placeholderText}>
                    {t("pages.shift_management.employee_name")}
                  </Typography>
                );
              }
              const emp = employees.find((e) => e.employee_id === selected);
              return (
                <Typography sx={styles.selectedText}>
                  {emp ? emp.name : selected}
                </Typography>
              );
            }}
            sx={styles.select}
          >
            <MenuItem value={FILTER_OPTIONS.ALL}>
              {t("pages.shift_management.all_employees")}
            </MenuItem>
            {employees.map((emp) => (
              <MenuItem key={emp.employee_id} value={emp.employee_id}>
                {emp.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Date Navigator */}
        <Box sx={styles.dateNavigator}>
          <IconButton size="small" sx={styles.iconButton} onClick={handlePrev}>
            <ChevronLeft fontSize="small" />
          </IconButton>
          <Typography sx={styles.dateLabel}>{dateLabel}</Typography>
          <IconButton size="small" sx={styles.iconButton} onClick={handleNext}>
            <ChevronRight fontSize="small" />
          </IconButton>
        </Box>

        {/* View Toggle */}
        <Button
          onClick={() => {
            setView(VIEW_MODES.WEEK);
            setCurrentDate(new Date());
          }}
          sx={styles.viewToggle(isWeekView)}
        >
          {t("pages.shift_management.week")}
        </Button>
      </Box>

      {/* Calendar View */}
      <Box sx={styles.calendarContainer}>
        <CalendarWeekView
          selectedDate={currentDate}
          employees={filteredEmployees}
          shifts={shifts}
          shiftTemplates={[
            { shift_type: t("layout.attendance.add_attendance") },
            { shift_type: t("layout.attendance.apply_leave") },
          ]}
          onAssignShift={handleAssignShift}
          onDeleteShift={handleDeleteShift}
          onShiftResize={handleShiftResize}
          onVisibleDateChange={setCurrentDate}
          enableAddFunc={(date) => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const checkDate = new Date(date);
            checkDate.setHours(0, 0, 0, 0);
            return checkDate.getTime() === today.getTime();
          }}
          onShiftClick={handleShiftClick}
          renderEventContent={(shift) => {
            if (shift.status === ATTENDANCE_STATUS.LEAVE) {
              return (
                <Box sx={styles.leaveEvent}>
                  <Typography sx={styles.leaveText}>L</Typography>
                </Box>
              );
            } else if (shift.isAttendance) {
              if (shift.status === ATTENDANCE_STATUS.COMPLETED) {
                return (
                  <Box sx={styles.completedEvent}>
                    <CheckCircle sx={styles.checkIcon} />

                    <Box sx={styles.timeBar}>
                      <Typography sx={styles.timeText}>
                        {convertTo12Hour(shift.check_in_time)} -{" "}
                        {convertTo12Hour(shift.check_out_time)}
                      </Typography>
                    </Box>
                  </Box>
                );
              } else {
                return (
                  <Box sx={styles.ongoingEvent}>
                    <Typography sx={styles.ongoingTimeText}>
                      {convertTo12Hour(shift.check_in_time)} -
                    </Typography>
                    <Typography sx={styles.ongoingTimeText}>
                      {t("pages.shift_management.ongoing")}
                    </Typography>
                  </Box>
                );
              }
            }
          }}
          enableColSpan={false}
        />
      </Box>
      <LeavePopup
        open={isLeavePopupOpen}
        onClose={() => setIsLeavePopupOpen(false)}
        onSubmit={handleLeaveSubmit}
        initialDate={leaveData?.date}
      />
    </Box>
  );
};

export default EmployeeAttendance;
